/**
 * キャプションテンプレート変数置換 + 100 文字制限 (純粋関数)。
 *
 * SPEC §4.3 準拠:
 * - `[年式]`, `[車種]`, `[グレード]`, `[走行距離]`, `[ホイール]`,
 *   `[装備1]`, `[装備2]`, `[アピール1]`, `[アピール2]`, `[アピール3]` を置換
 * - 置換後 100 文字超過時は末尾から削り `...` で終える (計 100 文字)
 * - 未使用の `[xxx]` が残ったら空文字に置換し、余分な空白を整える
 */

import type { VehicleMetadata } from '@shared/types';

const MAX_CAPTION_LENGTH = 100;
const ELLIPSIS = '...';

/** プレースホルダとその値の計算関数 */
interface Placeholder {
  readonly key: string;
  readonly value: string;
}

export function renderCaption(template: string, metadata: VehicleMetadata): string {
  const placeholders = buildPlaceholders(metadata);
  let result = template;
  for (const { key, value } of placeholders) {
    result = result.split(key).join(value);
  }
  // 未解決のプレースホルダは除去
  result = result.replace(/\[[^\]]+\]/g, '');
  // 連続空白 / 全角空白も含めて整形
  result = result.replace(/[ \u3000]{2,}/g, ' ').trim();
  return truncate(result, MAX_CAPTION_LENGTH);
}

export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - ELLIPSIS.length) + ELLIPSIS;
}

function buildPlaceholders(metadata: VehicleMetadata): readonly Placeholder[] {
  const wheel = findWheel(metadata.equipment);
  const eq1 = metadata.equipment[0] ?? '';
  const eq2 = metadata.equipment[1] ?? '';
  return [
    { key: '[年式]', value: `${metadata.year}年式` },
    { key: '[車種]', value: metadata.model },
    { key: '[グレード]', value: metadata.grade },
    { key: '[走行距離]', value: formatMileage(metadata.mileage) },
    { key: '[ホイール]', value: wheel },
    { key: '[装備1]', value: eq1 },
    { key: '[装備2]', value: eq2 },
    { key: '[アピール1]', value: metadata.appeals[0] },
    { key: '[アピール2]', value: metadata.appeals[1] },
    { key: '[アピール3]', value: metadata.appeals[2] },
  ];
}

function formatMileage(mileage: number): string {
  if (!Number.isFinite(mileage) || mileage < 0) return '0';
  return Math.trunc(mileage).toLocaleString('en-US');
}

function findWheel(equipment: readonly string[]): string {
  const hit = equipment.find((item) => /インチ/.test(item) || /ホイール/.test(item));
  return hit ?? 'ホイール';
}
